import { useState, useEffect } from 'react';
import { dbSurgeries, type Surgery } from '../../lib/localDB';
import { useNavigate } from 'react-router-dom';
import FinancialTab from './components/FinancialTab';

type AnalyticsData = {
  totalSurgeries: number;
  totalImplants: number;
  failedImplants: number;
  successRate: number;
  genderDistribution: { male: number; female: number };
  monthlyData: Array<{ month: string; implants: number; failures: number; surgeries: number }>;
  brandData: Array<{ name: string; total: number; failed: number; success: number; successRate: number }>;
  ageDistribution: Array<{ range: string; count: number; failed: number }>;
  riskFactors: Array<{ factor: string; value: number }>;
  failureProfiles: Array<{
    gender: string;
    age: string;
    seker: string;
    smoker: string;
    brand: string;
    failDate: string;
    operationDate: string;
    daysTofail: number;
  }>;
  soketData: Array<{ level: string; total: number; failed: number }>;
  boneDensityData: Array<{ type: string; total: number; failed: number }>;
};

export default function Analytics() {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'brands' | 'patients' | 'financial'>('overview');
  const navigate = useNavigate();

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const surgeries = await dbSurgeries.getAll();
      if (surgeries.length === 0) {
        setAnalytics(null);
        setLoading(false);
        return;
      }
      processAnalytics(surgeries);
    } catch (e) {
      console.error('Analytics yüklenemedi:', e);
    } finally {
      setLoading(false);
    }
  };

  const processAnalytics = (surgeries: Surgery[]) => {
    type FlatTooth = {
      surgery_id: string;
      implant_brand: string;
      bone_density: string;
      soket: string;
      greft: string;
      failed: boolean;
      fail_date: string | null;
      fail_days: number | null;
    };

    const allTeeth: FlatTooth[] = surgeries.flatMap((s) =>
      (s.teeth || []).map((t) => ({
        surgery_id: s.id,
        implant_brand: t.implant_brand || 'Bilinmiyor',
        bone_density: t.kemik || 'Bilinmiyor',
        soket: t.soket,
        greft: t.greft,
        failed: t.failed === true,
        fail_date: t.fail_date || null,
        fail_days: t.fail_days || null,
      }))
    );

    const failedTeeth = allTeeth.filter((t) => t.failed);
    const successRate =
      allTeeth.length > 0
        ? ((allTeeth.length - failedTeeth.length) / allTeeth.length) * 100
        : 100;

    const genderDistribution = {
      male: surgeries.filter((s) => s.cinsiyet === 'e').length,
      female: surgeries.filter((s) => s.cinsiyet === 'k').length,
    };

    // Monthly data
    const monthlyMap: { [key: string]: { implants: number; failures: number; surgeries: number } } = {};
    surgeries.forEach((surgery) => {
      const date = new Date(surgery.operation_date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (!monthlyMap[monthKey]) monthlyMap[monthKey] = { implants: 0, failures: 0, surgeries: 0 };
      monthlyMap[monthKey].surgeries++;
      const surgTeeth = allTeeth.filter((t) => t.surgery_id === surgery.id);
      monthlyMap[monthKey].implants += surgTeeth.length;
      monthlyMap[monthKey].failures += surgTeeth.filter((t) => t.failed).length;
    });

    const monthlyData = Object.entries(monthlyMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-12)
      .map(([key, data]) => {
        const [year, month] = key.split('-');
        const date = new Date(parseInt(year), parseInt(month) - 1);
        return { month: date.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' }), ...data };
      });

    // Brand data
    const brandMap: { [key: string]: { total: number; failed: number } } = {};
    allTeeth.forEach((tooth) => {
      if (!brandMap[tooth.implant_brand]) brandMap[tooth.implant_brand] = { total: 0, failed: 0 };
      brandMap[tooth.implant_brand].total++;
      if (tooth.failed) brandMap[tooth.implant_brand].failed++;
    });
    const brandData = Object.entries(brandMap)
      .map(([name, data]) => ({
        name,
        total: data.total,
        failed: data.failed,
        success: data.total - data.failed,
        successRate: ((data.total - data.failed) / data.total) * 100,
      }))
      .sort((a, b) => b.total - a.total);

    const yasLabels: Record<string, string> = {
      '1': '18-35',
      '2': '36-50',
      '3': '51-65',
      '4': '66+',
    };

    const ageDistribution = ['1', '2', '3', '4'].map((yasKey) => {
      const rangeTeeth = allTeeth.filter((t) => {
        const surgery = surgeries.find((s) => s.id === t.surgery_id);
        return surgery && surgery.yas === yasKey;
      });
      return {
        range: yasLabels[yasKey],
        count: rangeTeeth.length,
        failed: rangeTeeth.filter((t) => t.failed).length,
      };
    });

    const smokerTeeth = allTeeth.filter((t) => {
      const s = surgeries.find((su) => su.id === t.surgery_id);
      return s && s.sigara !== '0';
    });
    const nonSmokerTeeth = allTeeth.filter((t) => {
      const s = surgeries.find((su) => su.id === t.surgery_id);
      return s && s.sigara === '0';
    });

    const soketData = [
      { level: 'Taze Çekim (E)', total: allTeeth.filter((t) => t.soket === 'e').length, failed: allTeeth.filter((t) => t.soket === 'e' && t.failed).length },
      { level: 'Normal (H)', total: allTeeth.filter((t) => t.soket === 'h').length, failed: allTeeth.filter((t) => t.soket === 'h' && t.failed).length },
    ];

    const boneDensityMap: { [key: string]: { total: number; failed: number } } = {};
    allTeeth.forEach((tooth) => {
      const density = `Tip ${tooth.bone_density}`;
      if (!boneDensityMap[density]) boneDensityMap[density] = { total: 0, failed: 0 };
      boneDensityMap[density].total++;
      if (tooth.failed) boneDensityMap[density].failed++;
    });
    const boneDensityData = Object.entries(boneDensityMap).map(([type, data]) => ({
      type, total: data.total, failed: data.failed,
    }));

    const smokerFailRate = smokerTeeth.length > 0 ? (smokerTeeth.filter((t) => t.failed).length / smokerTeeth.length) * 100 : 0;
    const nonSmokerFailRate = nonSmokerTeeth.length > 0 ? (nonSmokerTeeth.filter((t) => t.failed).length / nonSmokerTeeth.length) * 100 : 0;
    const highSugarTeeth = allTeeth.filter((t) => { const s = surgeries.find((su) => su.id === t.surgery_id); return s && s.seker === '2'; });
    const highSugarFailRate = highSugarTeeth.length > 0 ? (highSugarTeeth.filter((t) => t.failed).length / highSugarTeeth.length) * 100 : 0;
    const greftTeeth = allTeeth.filter((t) => t.greft === 'e');
    const greftFailRate = greftTeeth.length > 0 ? (greftTeeth.filter((t) => t.failed).length / greftTeeth.length) * 100 : 0;
    const elderTeeth = allTeeth.filter((t) => { const s = surgeries.find((su) => su.id === t.surgery_id); return s && s.yas === '4'; });
    const elderFailRate = elderTeeth.length > 0 ? (elderTeeth.filter((t) => t.failed).length / elderTeeth.length) * 100 : 0;

    const riskFactors = [
      { factor: 'Sigara Kullanımı', value: smokerFailRate },
      { factor: 'Sigara Yok', value: nonSmokerFailRate },
      { factor: 'Yüksek Şeker (Tip 2)', value: highSugarFailRate },
      { factor: 'Greftleme', value: greftFailRate },
      { factor: 'İleri Yaş (60+)', value: elderFailRate },
    ];

    const failureProfiles = failedTeeth.map((tooth) => {
      const surgery = surgeries.find((s) => s.id === tooth.surgery_id);
      if (!surgery) return null;
      return {
        gender: surgery.cinsiyet === 'e' ? 'Erkek' : 'Kadın',
        age: yasLabels[surgery.yas] || surgery.yas,
        seker: surgery.seker === '0' ? 'Normal' : surgery.seker === '1' ? 'Yüksek' : 'Çok Yüksek',
        smoker: surgery.sigara === '0' ? 'Hayır' : surgery.sigara === '1' ? 'Az' : 'Çok',
        brand: tooth.implant_brand,
        failDate: tooth.fail_date || '-',
        operationDate: surgery.operation_date,
        daysTofail: tooth.fail_days || 0,
      };
    }).filter(Boolean) as any[];
    failureProfiles.sort((a, b) => a.daysTofail - b.daysTofail);

    setAnalytics({
      totalSurgeries: surgeries.length,
      totalImplants: allTeeth.length,
      failedImplants: failedTeeth.length,
      successRate,
      genderDistribution,
      monthlyData,
      brandData,
      ageDistribution,
      riskFactors,
      failureProfiles,
      soketData,
      boneDensityData,
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Genel Bakış', icon: 'ri-dashboard-line' },
    { id: 'brands', label: 'Marka Analizi', icon: 'ri-price-tag-3-line' },
    { id: 'patients', label: 'Hasta & Risk Analizi', icon: 'ri-user-heart-line' },
    { id: 'financial', label: 'Finansal', icon: 'ri-money-dollar-circle-line' },
  ];

  const getSuccessBadge = (rate: number) => {
    if (rate >= 90) return 'bg-green-100 text-green-700';
    if (rate >= 70) return 'bg-yellow-100 text-yellow-700';
    return 'bg-red-100 text-red-700';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50">
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center">
                <i className="ri-bar-chart-line text-2xl text-white"></i>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Analizler</h1>
                <p className="text-xs text-gray-500">İstatistikler ve raporlar</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2.5 text-gray-700 hover:bg-gray-100 rounded-lg transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-dashboard-line text-lg"></i>
                Dashboard
              </button>
              <button
                onClick={() => navigate('/')}
                className="px-4 py-2.5 text-gray-700 hover:bg-gray-100 rounded-lg transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-home-line text-lg"></i>
                Anasayfa
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tab Navigation */}
        <div className="flex gap-2 mb-8 bg-white p-1.5 rounded-xl w-fit">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`px-5 py-2.5 rounded-lg font-medium text-sm transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer ${
                activeTab === tab.id
                  ? 'bg-teal-500 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <i className={`${tab.icon} text-lg`}></i>
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'financial' ? (
          <FinancialTab />
        ) : !analytics ? (
          <div className="bg-white rounded-2xl p-12 text-center">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-bar-chart-line text-4xl text-gray-400"></i>
            </div>
            <p className="text-gray-600 text-lg">Henüz analiz edilecek veri yok</p>
            <p className="text-gray-400 text-sm mt-2">Kayıt ekledikten sonra analizler burada görünecek</p>
          </div>
        ) : (
          <>
            {/* ── OVERVIEW TAB ── */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  {[
                    { label: 'Toplam Ameliyat', value: analytics.totalSurgeries, color: 'teal', icon: 'ri-surgical-mask-line' },
                    { label: 'Toplam İmplant', value: analytics.totalImplants, color: 'cyan', icon: 'ri-tooth-line' },
                    { label: 'Başarılı', value: analytics.totalImplants - analytics.failedImplants, color: 'green', icon: 'ri-check-double-line', textColor: 'text-green-600' },
                    { label: 'Başarısız', value: analytics.failedImplants, color: 'red', icon: 'ri-close-circle-line', textColor: 'text-red-600' },
                    { label: 'Başarı Oranı', value: `${analytics.successRate.toFixed(1)}%`, color: 'teal', icon: 'ri-percent-line', textColor: 'text-teal-600' },
                  ].map((card) => (
                    <div key={card.label} className="bg-white rounded-2xl p-5">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm text-gray-500">{card.label}</span>
                        <div className={`w-10 h-10 bg-${card.color}-100 rounded-lg flex items-center justify-center`}>
                          <i className={`${card.icon} text-xl text-${card.color}-600`}></i>
                        </div>
                      </div>
                      <p className={`text-3xl font-bold ${card.textColor ?? 'text-gray-900'}`}>{card.value}</p>
                    </div>
                  ))}
                </div>

                {/* Gender + Age summary row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Gender */}
                  <div className="bg-white rounded-2xl p-6">
                    <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <i className="ri-user-line text-teal-600"></i>
                      Cinsiyet Dağılımı
                    </h3>
                    <div className="space-y-3">
                      {[
                        { label: 'Erkek', count: analytics.genderDistribution.male, total: analytics.totalSurgeries, icon: 'ri-men-line', color: 'teal' },
                        { label: 'Kadın', count: analytics.genderDistribution.female, total: analytics.totalSurgeries, icon: 'ri-women-line', color: 'pink' },
                      ].map((g) => {
                        const pct = analytics.totalSurgeries > 0 ? (g.count / analytics.totalSurgeries) * 100 : 0;
                        return (
                          <div key={g.label} className="flex items-center gap-4">
                            <div className={`w-8 h-8 flex items-center justify-center bg-${g.color}-100 rounded-lg`}>
                              <i className={`${g.icon} text-${g.color}-600`}></i>
                            </div>
                            <div className="flex-1">
                              <div className="flex justify-between text-sm mb-1">
                                <span className="font-medium text-gray-700">{g.label}</span>
                                <span className="text-gray-500">{g.count} ameliyat ({pct.toFixed(0)}%)</span>
                              </div>
                              <div className="w-full bg-gray-100 rounded-full h-2">
                                <div className={`bg-${g.color}-400 h-2 rounded-full`} style={{ width: `${pct}%` }}></div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Age Distribution */}
                  <div className="bg-white rounded-2xl p-6">
                    <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <i className="ri-calendar-line text-teal-600"></i>
                      Yaş Grubu Dağılımı
                    </h3>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-100">
                          <th className="text-left py-2 font-semibold text-gray-600">Yaş Grubu</th>
                          <th className="text-center py-2 font-semibold text-gray-600">İmplant</th>
                          <th className="text-center py-2 font-semibold text-gray-600">Başarısız</th>
                          <th className="text-center py-2 font-semibold text-gray-600">Başarı %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.ageDistribution.map((row) => {
                          const rate = row.count > 0 ? ((row.count - row.failed) / row.count) * 100 : 100;
                          return (
                            <tr key={row.range} className="border-b border-gray-50">
                              <td className="py-2.5 font-medium text-gray-800">{row.range}</td>
                              <td className="py-2.5 text-center text-gray-600">{row.count}</td>
                              <td className="py-2.5 text-center text-red-500">{row.failed}</td>
                              <td className="py-2.5 text-center">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getSuccessBadge(rate)}`}>
                                  {rate.toFixed(1)}%
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Monthly Summary Table */}
                <div className="bg-white rounded-2xl p-6">
                  <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <i className="ri-calendar-2-line text-teal-600"></i>
                    Aylık Özet
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 px-3 font-semibold text-gray-700">Dönem</th>
                          <th className="text-center py-3 px-3 font-semibold text-gray-700">Ameliyat</th>
                          <th className="text-center py-3 px-3 font-semibold text-gray-700">İmplant</th>
                          <th className="text-center py-3 px-3 font-semibold text-gray-700">Başarısız</th>
                          <th className="text-center py-3 px-3 font-semibold text-gray-700">Başarı Oranı</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...analytics.monthlyData].reverse().map((row, i) => {
                          const rate = row.implants > 0 ? ((row.implants - row.failures) / row.implants) * 100 : 100;
                          return (
                            <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                              <td className="py-3 px-3 font-medium text-gray-800">{row.month}</td>
                              <td className="py-3 px-3 text-center text-gray-600">{row.surgeries}</td>
                              <td className="py-3 px-3 text-center text-teal-600 font-medium">{row.implants}</td>
                              <td className="py-3 px-3 text-center text-red-500">{row.failures}</td>
                              <td className="py-3 px-3 text-center">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getSuccessBadge(rate)}`}>
                                  {row.implants > 0 ? `${rate.toFixed(1)}%` : '—'}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ── BRANDS TAB ── */}
            {activeTab === 'brands' && (
              <div className="space-y-6">
                {analytics.brandData.length === 0 ? (
                  <div className="bg-white rounded-2xl p-10 text-center text-gray-500">Henüz marka verisi yok</div>
                ) : (
                  <>
                    {/* Summary cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-white rounded-2xl p-5">
                        <p className="text-xs text-gray-500 mb-1">Toplam Marka</p>
                        <p className="text-2xl font-bold text-gray-900">{analytics.brandData.length}</p>
                      </div>
                      <div className="bg-white rounded-2xl p-5">
                        <p className="text-xs text-gray-500 mb-1">En Çok Kullanılan</p>
                        <p className="text-lg font-bold text-teal-600 truncate">{analytics.brandData[0]?.name}</p>
                        <p className="text-xs text-gray-400">{analytics.brandData[0]?.total} implant</p>
                      </div>
                      <div className="bg-white rounded-2xl p-5">
                        <p className="text-xs text-gray-500 mb-1">En Yüksek Başarı</p>
                        <p className="text-lg font-bold text-green-600 truncate">
                          {[...analytics.brandData].sort((a, b) => b.successRate - a.successRate)[0]?.name}
                        </p>
                        <p className="text-xs text-gray-400">
                          {[...analytics.brandData].sort((a, b) => b.successRate - a.successRate)[0]?.successRate.toFixed(1)}%
                        </p>
                      </div>
                      <div className="bg-white rounded-2xl p-5">
                        <p className="text-xs text-gray-500 mb-1">En Düşük Başarı</p>
                        <p className="text-lg font-bold text-red-500 truncate">
                          {[...analytics.brandData].sort((a, b) => a.successRate - b.successRate)[0]?.name}
                        </p>
                        <p className="text-xs text-gray-400">
                          {[...analytics.brandData].sort((a, b) => a.successRate - b.successRate)[0]?.successRate.toFixed(1)}%
                        </p>
                      </div>
                    </div>

                    {/* Brand table */}
                    <div className="bg-white rounded-2xl p-6">
                      <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <i className="ri-table-line text-teal-600"></i>
                        Marka Detayları
                      </h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-200">
                              <th className="text-left py-3 px-4 font-semibold text-gray-700">Marka</th>
                              <th className="text-center py-3 px-4 font-semibold text-gray-700">Toplam</th>
                              <th className="text-center py-3 px-4 font-semibold text-gray-700">Başarılı</th>
                              <th className="text-center py-3 px-4 font-semibold text-gray-700">Başarısız</th>
                              <th className="text-center py-3 px-4 font-semibold text-gray-700">Başarı Oranı</th>
                              <th className="py-3 px-4 font-semibold text-gray-700">Dağılım</th>
                            </tr>
                          </thead>
                          <tbody>
                            {analytics.brandData.map((brand, index) => (
                              <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                                <td className="py-3 px-4 font-medium text-gray-900">{brand.name}</td>
                                <td className="py-3 px-4 text-center text-gray-600">{brand.total}</td>
                                <td className="py-3 px-4 text-center text-green-600 font-medium">{brand.success}</td>
                                <td className="py-3 px-4 text-center text-red-500 font-medium">{brand.failed}</td>
                                <td className="py-3 px-4 text-center">
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSuccessBadge(brand.successRate)}`}>
                                    {brand.successRate.toFixed(1)}%
                                  </span>
                                </td>
                                <td className="py-3 px-4 min-w-[120px]">
                                  <div className="w-full bg-gray-100 rounded-full h-2">
                                    <div
                                      className="bg-teal-400 h-2 rounded-full"
                                      style={{ width: `${analytics.totalImplants > 0 ? (brand.total / analytics.totalImplants) * 100 : 0}%` }}
                                    ></div>
                                  </div>
                                  <p className="text-xs text-gray-400 mt-0.5">
                                    {analytics.totalImplants > 0 ? ((brand.total / analytics.totalImplants) * 100).toFixed(0) : 0}% payı
                                  </p>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ── PATIENTS & RISK TAB ── */}
            {activeTab === 'patients' && (
              <div className="space-y-6">
                {/* Risk Factors Table */}
                <div className="bg-white rounded-2xl p-6">
                  <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <i className="ri-alert-line text-teal-600"></i>
                    Risk Faktörlerine Göre Başarısızlık Oranı
                  </h3>
                  <div className="space-y-3">
                    {analytics.riskFactors.map((rf) => (
                      <div key={rf.factor} className="flex items-center gap-4">
                        <span className="w-44 text-sm text-gray-700 shrink-0">{rf.factor}</span>
                        <div className="flex-1 bg-gray-100 rounded-full h-3">
                          <div
                            className={`h-3 rounded-full ${rf.value >= 30 ? 'bg-red-400' : rf.value >= 10 ? 'bg-yellow-400' : 'bg-green-400'}`}
                            style={{ width: `${Math.min(rf.value, 100)}%` }}
                          ></div>
                        </div>
                        <span className={`text-sm font-semibold w-12 text-right ${rf.value >= 30 ? 'text-red-600' : rf.value >= 10 ? 'text-yellow-600' : 'text-green-600'}`}>
                          {rf.value.toFixed(1)}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Soket + Bone Density */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-white rounded-2xl p-6">
                    <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <i className="ri-tooth-line text-teal-600"></i>
                      Soket Durumu Analizi
                    </h3>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-100">
                          <th className="text-left py-2 font-semibold text-gray-600">Soket</th>
                          <th className="text-center py-2 font-semibold text-gray-600">Toplam</th>
                          <th className="text-center py-2 font-semibold text-gray-600">Başarısız</th>
                          <th className="text-center py-2 font-semibold text-gray-600">Başarı %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.soketData.map((row) => {
                          const rate = row.total > 0 ? ((row.total - row.failed) / row.total) * 100 : 100;
                          return (
                            <tr key={row.level} className="border-b border-gray-50">
                              <td className="py-2.5 font-medium text-gray-800">{row.level}</td>
                              <td className="py-2.5 text-center text-gray-600">{row.total}</td>
                              <td className="py-2.5 text-center text-red-500">{row.failed}</td>
                              <td className="py-2.5 text-center">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getSuccessBadge(rate)}`}>
                                  {row.total > 0 ? `${rate.toFixed(1)}%` : '—'}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="bg-white rounded-2xl p-6">
                    <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <i className="ri-layers-line text-teal-600"></i>
                      Kemik Tipi Analizi
                    </h3>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-100">
                          <th className="text-left py-2 font-semibold text-gray-600">Kemik Tipi</th>
                          <th className="text-center py-2 font-semibold text-gray-600">Toplam</th>
                          <th className="text-center py-2 font-semibold text-gray-600">Başarısız</th>
                          <th className="text-center py-2 font-semibold text-gray-600">Başarı %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.boneDensityData.map((row) => {
                          const rate = row.total > 0 ? ((row.total - row.failed) / row.total) * 100 : 100;
                          return (
                            <tr key={row.type} className="border-b border-gray-50">
                              <td className="py-2.5 font-medium text-gray-800">{row.type}</td>
                              <td className="py-2.5 text-center text-gray-600">{row.total}</td>
                              <td className="py-2.5 text-center text-red-500">{row.failed}</td>
                              <td className="py-2.5 text-center">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getSuccessBadge(rate)}`}>
                                  {row.total > 0 ? `${rate.toFixed(1)}%` : '—'}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Failure Profiles Table */}
                <div className="bg-white rounded-2xl p-6">
                  <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <i className="ri-close-circle-line text-red-500"></i>
                    Başarısızlık Profilleri
                    <span className="ml-1 text-xs font-normal text-gray-400">({analytics.failureProfiles.length} kayıt)</span>
                  </h3>
                  {analytics.failureProfiles.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">Henüz başarısızlık kaydı yok</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-3 px-4 font-semibold text-gray-700">Gün</th>
                            <th className="text-left py-3 px-4 font-semibold text-gray-700">Cinsiyet</th>
                            <th className="text-left py-3 px-4 font-semibold text-gray-700">Yaş</th>
                            <th className="text-left py-3 px-4 font-semibold text-gray-700">Şeker</th>
                            <th className="text-left py-3 px-4 font-semibold text-gray-700">Sigara</th>
                            <th className="text-left py-3 px-4 font-semibold text-gray-700">Marka</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analytics.failureProfiles.slice(0, 20).map((profile, index) => (
                            <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="py-3 px-4">
                                <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium whitespace-nowrap">
                                  {profile.daysTofail} gün
                                </span>
                              </td>
                              <td className="py-3 px-4 text-gray-800">{profile.gender}</td>
                              <td className="py-3 px-4 text-gray-800">{profile.age}</td>
                              <td className="py-3 px-4 text-gray-800">{profile.seker}</td>
                              <td className="py-3 px-4 text-gray-800">{profile.smoker}</td>
                              <td className="py-3 px-4 text-gray-800">{profile.brand}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
